import { computed, inject } from '@angular/core';
import { Routes } from '@angular/router';
import {
  anonymousOnlyGuard,
  authGuard,
  authenticatedMatch,
  pendingDeletionGuard,
  pendingDeletionOnlyGuard,
} from './core/auth/auth.guard';
import { CATALOG_ROUTES } from './features/catalog/catalog.routes';
import { ProjectStore } from './features/projects/project.store';
import { PublicLayoutComponent } from './layout/public-layout/public-layout.component';
import { ShellComponent } from './layout/shell/shell.component';

const comingSoon = () =>
  import('./features/system/coming-soon.page').then((module) => module.ComingSoonPage);
const linkAction = () =>
  import('./features/auth/link-action/link-action.page').then((module) => module.LinkActionPage);
const invitation = () =>
  import('./features/invitations/invitation.page').then((module) => module.InvitationPage);
const projectName = () => {
  const store = inject(ProjectStore);
  return computed(() => store.name() ?? 'Commessa');
};
const projectTitle = () => inject(ProjectStore).name;
const notFound = () =>
  import('./features/system/not-found.page').then((module) => module.NotFoundPage);

/**
 * Rotte (contracts/ui-shell-navigation.md). Gli indirizzi sono identificatori in inglese; titoli
 * e percorsi sono in italiano. Le pagine pubbliche stanno sotto `PublicLayout`, quelle protette
 * sotto `ShellComponent` con `authGuard`.
 */
export const routes: Routes = [
  // La struttura interna viene prima: con l'indirizzo vuoto il router non deve fermarsi sul
  // layout pubblico, che non ha una pagina per `/`.
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard, pendingDeletionGuard],
    data: { navContext: 'home', breadcrumb: 'Dashboard' },
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.page').then((module) => module.DashboardPage),
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/profile.routes').then((module) => module.PROFILE_ROUTES),
      },
      {
        path: 'subscription',
        title: 'Abbonamento · In arrivo',
        data: { breadcrumb: 'Abbonamento', feature: 'Abbonamento' },
        loadComponent: comingSoon,
      },
      {
        path: 'notifications',
        title: 'Notifiche · In arrivo',
        data: { breadcrumb: 'Notifiche', feature: 'Notifiche' },
        loadComponent: comingSoon,
      },
      {
        path: 'sites/new',
        title: 'Nuovo cantiere',
        data: { breadcrumb: 'Nuovo cantiere' },
        loadComponent: () =>
          import('./features/sites/site-form.page').then((module) => module.SiteFormPage),
      },
      {
        path: 'invites',
        title: 'Inviti',
        data: { breadcrumb: 'Inviti' },
        loadComponent: () =>
          import('./features/invitations/invites.page').then((module) => module.InvitesPage),
      },
      {
        path: 'sites/deleted',
        title: 'Cantieri eliminati',
        data: { breadcrumb: 'Cantieri eliminati' },
        loadComponent: () =>
          import('./features/sites/deleted-sites.page').then((module) => module.DeletedSitesPage),
      },
      {
        path: 'projects/:projectId',
        title: 'Commessa',
        data: { breadcrumb: projectName, titleDetail: projectTitle },
        loadComponent: () =>
          import('./features/projects/project.page').then((module) => module.ProjectPage),
      },
      {
        path: 'sites/:siteId',
        loadChildren: () =>
          import('./features/site/site.routes').then((module) => module.SITE_ROUTES),
      },
      {
        // Console degli operatori della piattaforma (spec 004).
        path: 'admin',
        loadChildren: () =>
          import('./features/admin/admin.routes').then((module) => module.ADMIN_ROUTES),
      },
      ...CATALOG_ROUTES,
    ],
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'login',
        title: 'Accedi',
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
          import('./features/auth/login/login.page').then((module) => module.LoginPage),
      },
      {
        path: 'register',
        title: 'Crea un account',
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
          import('./features/auth/register/register.page').then((module) => module.RegisterPage),
      },
      {
        path: 'check-email',
        title: 'Controlla la posta',
        loadComponent: () =>
          import('./features/auth/check-email/check-email.page').then(
            (module) => module.CheckEmailPage,
          ),
      },
      {
        path: 'verify-email',
        title: "Conferma dell'email",
        loadComponent: () =>
          import('./features/auth/verify-email/verify-email.page').then(
            (module) => module.VerifyEmailPage,
          ),
      },
      {
        path: 'forgot-password',
        title: 'Password dimenticata',
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
          import('./features/auth/forgot-password/forgot-password.page').then(
            (module) => module.ForgotPasswordPage,
          ),
      },
      {
        path: 'reset-password',
        title: 'Nuova password',
        loadComponent: () =>
          import('./features/auth/reset-password/reset-password.page').then(
            (module) => module.ResetPasswordPage,
          ),
      },
      {
        path: 'account-recovery',
        pathMatch: 'full',
        title: "Recupero dell'account",
        loadComponent: () =>
          import('./features/auth/account-recovery/account-recovery.page').then(
            (module) => module.AccountRecoveryPage,
          ),
      },
      {
        path: 'account-recovery/cancel',
        title: 'Annulla il recupero',
        data: { linkAction: 'account-recovery-cancel' },
        loadComponent: linkAction,
      },
      {
        path: 'recovery-email/confirm',
        title: "Conferma dell'email di recupero",
        data: { linkAction: 'recovery-email-confirm' },
        loadComponent: linkAction,
      },
      {
        path: 'email-change/confirm',
        title: 'Conferma della nuova email',
        data: { linkAction: 'email-change-confirm' },
        loadComponent: linkAction,
      },
      {
        path: 'email-change/cancel',
        title: "Annulla il cambio dell'email",
        data: { linkAction: 'email-change-cancel' },
        loadComponent: linkAction,
      },
      {
        path: 'email-change/revert',
        title: "Ripristino dell'email",
        data: { linkAction: 'email-change-revert' },
        loadComponent: linkAction,
      },
      {
        path: 'account/pending-deletion',
        title: 'Account in attesa di eliminazione',
        canActivate: [pendingDeletionOnlyGuard],
        loadComponent: () =>
          import('./features/account/pending-deletion.page').then(
            (module) => module.PendingDeletionPage,
          ),
      },
      {
        path: 'invitations',
        title: 'Invito',
        loadComponent: invitation,
      },
      {
        path: 'invitations/:id',
        title: 'Invito',
        loadComponent: invitation,
      },
      {
        path: 'terms',
        title: "Termini d'uso",
        data: { kind: 'terms' },
        loadComponent: () =>
          import('./features/legal/legal.page').then((module) => module.LegalPage),
      },
      {
        path: 'privacy',
        title: 'Informativa privacy',
        data: { kind: 'privacy' },
        loadComponent: () =>
          import('./features/legal/legal.page').then((module) => module.LegalPage),
      },
    ],
  },
  // Pagina non trovata: dentro la struttura per chi è autenticato, pubblica per gli altri.
  {
    path: '**',
    component: ShellComponent,
    canMatch: [authenticatedMatch],
    canActivate: [pendingDeletionGuard],
    data: { navContext: 'home', breadcrumb: 'Dashboard' },
    children: [
      {
        path: '**',
        title: 'Pagina non trovata',
        data: { breadcrumb: 'Pagina non trovata' },
        loadComponent: notFound,
      },
    ],
  },
  {
    path: '**',
    component: PublicLayoutComponent,
    children: [{ path: '**', title: 'Pagina non trovata', loadComponent: notFound }],
  },
];
